/**
 * Compute what will be lost when switching a campaign from one game type to another.
 *
 * Returns an ordered list of impact entries:
 *   { kind, label, severity, count? }
 *   - kind: machine identifier (used as React key)
 *   - label: human-readable description shown in the confirmation modal
 *   - severity: 'destructive' (irreversible / authored content) | 'warning' (replaced with defaults)
 *   - count: optional cardinality (e.g. "12 questions")
 *
 * Pure function — no React, no Firestore.
 */

// Fields that survive a game-type switch unconditionally. Limited to BRAND
// and SHARE assets — anything game-type-specific (title, intro, player,
// collectibles, obstacles, platforms, sounds, type-specific colours) resets
// to the new type's defaults so the campaign actually feels like the new game.
export const PRESERVED_OPTION_FIELDS = [
  // Branding / theme
  'theme', 'customColors', 'selectedStyle', 'selectedThemeName', 'selectedThemeCategory',
  'frameStyle', 'fontScale',
  // Layout / motion preferences (visual chrome, not game mechanics)
  'cardLayout', 'motionStyle', 'buttonVariant', 'buttonPosition',
  'startTitleColor', 'startBodyColor', 'startButtonColor', 'startTitleSize',
  'hideHeadline',
  // Leaderboard rules
  'leaderboard',
  // Customer's CTA / Action Button
  'ctaEnabled', 'cta_text', 'cta_link', 'cta',
  // Customer-uploaded brand artwork
  'coverPhoto', 'coverPhotoZoom', 'coverPhotoPosition',
  'sharingPhoto', 'shareTextOverride',
  'headlinePhoto', 'headline_url',
];

/**
 * Inspect what game-specific data is currently set on a campaign and would
 * not survive the switch. Returns `[]` when nothing will be lost.
 */
export function computeSwitchImpact({ currentGameTypeId, currentOptions = {}, newGameTypeId } = {}) {
  if (!currentGameTypeId || !newGameTypeId || currentGameTypeId === newGameTypeId) return [];
  const out = [];
  const o = currentOptions || {};

  // -------- Quiz Rush content --------
  if (currentGameTypeId === 'quizrush') {
    const qs = Array.isArray(o.questions) ? o.questions : [];
    // Don't warn if the only question is the boilerplate default that ships with the type.
    const isOnlyDefault = qs.length === 1 && (qs[0]?.id === 'q-default-1' || qs[0]?.questionText === 'Your first question?');
    if (qs.length > 0 && !isOnlyDefault) {
      out.push({
        kind: 'quiz-questions',
        label: `${qs.length} quiz question${qs.length === 1 ? '' : 's'}`,
        severity: 'destructive',
        count: qs.length,
      });
    }
  }

  // -------- Complete the List content --------
  if (currentGameTypeId === 'completelist') {
    const items = Array.isArray(o.listItems) ? o.listItems : [];
    if (items.length > 0) {
      out.push({
        kind: 'list-items',
        label: `${items.length} list item${items.length === 1 ? '' : 's'}${o.listTitle ? ` (“${o.listTitle}”)` : ''}`,
        severity: 'destructive',
        count: items.length,
      });
    } else if (o.listTitle) {
      out.push({ kind: 'list-title', label: `List title “${o.listTitle}”`, severity: 'warning' });
    }
  }

  // -------- Custom uploaded player character / animation --------
  // Only flag when frames are clearly user-uploaded (not the bundled defaults living under /assets/library).
  const isLibraryPath = (p) => typeof p === 'string' && (p.startsWith('/assets/library/') || p.startsWith('procedural:'));
  const customFrames = Array.isArray(o.playerAnimationFrames)
    ? o.playerAnimationFrames.filter((p) => p && !isLibraryPath(p))
    : [];
  if (customFrames.length > 0 || (o.playerImage && !isLibraryPath(o.playerImage))) {
    out.push({
      kind: 'player-character',
      label: 'Custom player character',
      severity: 'warning',
    });
  }

  // -------- Custom obstacle / platform / pipe artwork --------
  if (o.obstacleImage && !isLibraryPath(o.obstacleImage)) {
    out.push({ kind: 'obstacle-image', label: 'Custom obstacle artwork', severity: 'warning' });
  }
  const platformParts = [o.platformImageLeft, o.platformImageMiddle, o.platformImageRight].filter(Boolean);
  if (platformParts.some((p) => !isLibraryPath(p))) {
    out.push({ kind: 'platform-tiles', label: 'Custom platform tiles', severity: 'warning' });
  }
  if (o.pipeImage && !isLibraryPath(o.pipeImage)) {
    out.push({ kind: 'pipe-image', label: 'Custom pipe artwork', severity: 'warning' });
  }

  // -------- Game-specific settings (colors / mechanics that don't translate) --------
  if (o.gameSettings && Object.keys(o.gameSettings).length > 0) {
    out.push({
      kind: 'game-settings',
      label: `Game-specific colours${currentGameTypeId === 'snake' ? ' (snake / food / grid)' : currentGameTypeId === 'flappy' ? ' (pipe colour)' : ''}`,
      severity: 'warning',
    });
  }

  // -------- Snake skin --------
  if (currentGameTypeId === 'snake' && o.snakeSkin && o.snakeSkin.simple?.foodUrl) {
    out.push({ kind: 'snake-skin', label: 'Custom snake skin', severity: 'warning' });
  }

  // -------- Mini Golf custom hole layouts (if user authored any) --------
  if (currentGameTypeId === 'minigolf' && Array.isArray(o.holes) && o.holes.length > 0) {
    out.push({
      kind: 'minigolf-holes',
      label: `${o.holes.length} custom hole${o.holes.length === 1 ? '' : 's'}`,
      severity: 'destructive',
      count: o.holes.length,
    });
  }

  return out;
}
